import { tryCatch } from '@/types/utils';
import type { CommonResponse } from '@/types/response';
import type { Token } from '@/types/token';
import { getAssociatedTokenAddressSync, TOKEN_PROGRAM_ID } from '@solana/spl-token';
import type { Connection, ParsedAccountData, PublicKey } from '@solana/web3.js';

async function getSOLBalance({
  connection,
  owner,
}: {
  connection: Connection;
  owner?: PublicKey;
}): Promise<CommonResponse<number>> {
  return tryCatch<number>(async () => {
    if (!owner) {
      return { error: new Error('Owner not provided'), ok: false };
    }
    const balanceLamports = await connection.getBalance(owner);

    return { data: balanceLamports, ok: true };
  });
}

async function getTokensBalance({
  connection,
  owner,
  tokens,
}: {
  connection: Connection;
  owner?: PublicKey;
  tokens: Token[];
}): Promise<CommonResponse<Map<string, number>>> {
  return tryCatch<Map<string, number>>(async () => {
    if (!owner) {
      return { error: new Error('Owner not provided'), ok: false };
    }

    const PDA: PublicKey[] = [];
    for (const { mint } of tokens) {
      PDA.push(getAssociatedTokenPDA({ mint, owner }));
    }

    const multipleParsedAccounts = await connection.getMultipleParsedAccounts(PDA, {
      commitment: 'processed',
    });

    const balances = new Map<string, number>();

    for (const i in multipleParsedAccounts.value) {
      const account = multipleParsedAccounts.value[i];
      const token = tokens[i];

      if (!account) {
        continue;
      }
      if (!token) {
        continue;
      }

      const info = (account.data as ParsedAccountData).parsed.info;

      balances.set(info.mint, Number(info.tokenAmount.amount));
    }

    return { data: balances, ok: true };
  });
}

function getAssociatedTokenPDA({
  mint,
  owner,
  allowOwnerOffCurve = false,
  program = TOKEN_PROGRAM_ID,
}: {
  mint: PublicKey;
  owner: PublicKey;
  allowOwnerOffCurve?: boolean;
  program?: PublicKey;
}): PublicKey {
  return getAssociatedTokenAddressSync(mint, owner, allowOwnerOffCurve, program);
}

export const solanaTokenService = {
  getSOLBalance,
  getTokensBalance,
  getAssociatedTokenPDA,
};
